import { CustomDimensions } from "@core/analytics/analytics-dimension";
import { AnalyticsEvent, EventCategories } from "@core/analytics/analytics-event";
import { RouteNames } from "@core/analytics/analytics-view";
import { AnalyticsDimension, AnalyticsTracker } from "@core/analytics/trackers/analytics-tracker";
import { FeatureToggle, FeatureTogglesManager } from "@domain/feature-toggles/feature-toggles-manager";
import { observable } from "micro-observables";

export class AnalyticsService<E extends EventCategories, D extends CustomDimensions, V extends RouteNames> {
	private readonly areAnalyticsDisabled = observable(false);

	constructor(
		private readonly trackers: AnalyticsTracker[],
		private readonly eventCategories: E,
		private readonly customDimensions: D,
		private readonly routeNames: V,
		private readonly featureTogglesManager: FeatureTogglesManager,
	) {
		this.featureTogglesManager
			.hasFeature(FeatureToggle.DeactivateAnalytics)
			.subscribe(disabled => this.areAnalyticsDisabled.set(disabled));
	}

	trackEvent(event: AnalyticsEvent<E>): void {
		if (this.areAnalyticsDisabled.get()) {
			return;
		}
		this.trackers.forEach(tracker => {
			tracker.trackEvent({ ...event, category: this.eventCategories[event.category] });
		});
	}

	setCustomDimension(dimensionName: keyof D, dimensionValue: AnalyticsDimension): void {
		if (this.areAnalyticsDisabled.get()) {
			return;
		}
		this.trackers.forEach(tracker => tracker.setCustomDimension(this.customDimensions[dimensionName], dimensionValue));
	}

	trackView(viewTitle: keyof V, customUrl?: string): void {
		if (this.areAnalyticsDisabled.get()) {
			return;
		}
		this.trackers.forEach(tracker => tracker.trackView(this.routeNames[viewTitle], customUrl));
	}

	setUserId(userId: string | null): void {
		if (this.areAnalyticsDisabled.get()) {
			return;
		}
		this.trackers.forEach(tracker => tracker.setUserId(userId));
	}
}
